/**
 * ComparisonTable.tsx — sammenligning av pakker (funksjon mot pakke)
 *
 * Hver pakke har samme farge som i priskortene (tre nyanser av Carbon-blå): farget
 * topplinje og tonet bakgrunn i kolonneoverskriften, med pakkenavn og pris.
 * Anbefalt pakke har «Anbefalt»-etikett og tonet kolonne gjennom hele tabellen.
 * Rader er gruppert (gruppe-rader går over alle kolonner), og siste rad har
 * bestillingsknapper. Med/uten vises med fylt hake i pakkens farge og strek.
 *
 * Semantikk: <table> med <caption>, scope=col/row; wrapperen er scrollbar
 * og tastaturfokuserbar på smale skjermer. Med/uten har tekst for skjermlesere.
 */

import { CheckmarkFilled, Subtract } from '@carbon/icons-react';
import LandingButton from '@/components/landing/LandingButton';
import type { ProductComparison, ProductPlans } from '@/types/product';
import SectionHeader from './SectionHeader';
import shared from './product.module.css';
import styles from './plans.module.css';

interface ComparisonTableProps {
  comparison: ProductComparison;
  plans: ProductPlans;
}

export default function ComparisonTable({ comparison, plans }: ComparisonTableProps) {
  const columns = plans.items.length + 1;

  return (
    // Seksjonen har ikke eget navn: tabell-wrapperen under er regionen (unngår to like landemerker)
    <section id="sammenlign" className={`${shared.section} ${styles.tiers}`}>
      <div className={shared.container}>
        <SectionHeader id="comparison-heading" eyebrow={comparison.eyebrow} heading={comparison.heading}>
          <p className={shared.lead}>{comparison.intro}</p>
        </SectionHeader>

        <div className={styles.tableWrap} tabIndex={0} role="region" aria-labelledby="comparison-heading">
          <table className={styles.table}>
            <caption className={shared.srOnly}>{comparison.heading}</caption>
            <thead>
              <tr>
                <th scope="col" className={styles.thFeature}>
                  {comparison.featureLabel}
                </th>
                {plans.items.map((plan) => (
                  <th
                    key={plan.id}
                    scope="col"
                    className={`${styles.thPlan} ${plan.highlighted ? `${styles.colHighlight} ${styles.thFeatured} ${shared.featured}` : ''}`}
                  >
                    {plan.highlighted && plan.highlightLabel && (
                      <span className={shared.featuredEyebrow}>{plan.highlightLabel}</span>
                    )}
                    <span className={styles.thName}>{plan.name}</span>
                    <span className={styles.thPrice}>
                      {plans.fromLabel} {plan.price} {plan.priceNote}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparison.rows.map((row) =>
                row.group ? (
                  <tr key={row.label} className={styles.groupRow}>
                    <th scope="colgroup" colSpan={columns}>
                      {row.label}
                    </th>
                  </tr>
                ) : (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    {row.values.map((value, i) => (
                      <td key={plans.items[i].id} className={plans.items[i].highlighted ? styles.colHighlight : ''}>
                        {typeof value === 'boolean' ? (
                          <>
                            {value ? (
                              <CheckmarkFilled size={20} aria-hidden className={styles.yes} />
                            ) : (
                              <Subtract size={20} aria-hidden className={styles.no} />
                            )}
                            <span className={shared.srOnly}>{value ? comparison.yes : comparison.no}</span>
                          </>
                        ) : (
                          <span className={styles.value}>{value}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ),
              )}
              <tr className={styles.ctaRow}>
                <td />
                {plans.items.map((plan) => (
                  <td key={plan.id} className={plan.highlighted ? styles.colHighlight : ''}>
                    <LandingButton link={plan.cta} kind={plan.highlighted ? 'primary' : 'tertiary'} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
